import { Navigate, useParams } from "react-router";
import { Route, Routes } from "react-router-dom";
import Quarterly from "reports/pages/customers/qiagen/Quarterly";
import PciMachinePipeline from "reports/pages/customers/pci/Pipeline/PciMachinePipeline";
import SterisWeekly from "reports/pages/customers/steris/SterisWeekly";
import WeekToDateReportRoute from "reports/pages/GenericReports/WeekToDate/WeekToDateReportRoute";
import NWEos from "reports/pages/customers/naturesway/eos/NWEos";
import PciukEos from "reports/pages/customers/pciuk/eos/PciukEos";
import PciukWeeklySku from "reports/pages/customers/pciuk/weeklySku/PciukWeeklySku";
import SbdwarringtonEOS from "reports/pages/customers/sbdwarrington/eos/SbdwarringtonEOS";
import KrugerEOS from "reports/pages/customers/kruger/eos/KrugerEOS";
import MonthlyPerformanceReport from "reports/components/Reports/BespokeReports/Reports/MonthlyPerformanceReport";
import NbOperatorTPM from "reports/pages/customers/nb/NbOperatorTPM";
import SkuAndMetricReport from "reports/components/Reports/SkuAndMetricReport";
import KrugerTagging from "reports/pages/customers/kruger/tagging/KrugerTagging";
import GenericEOS from "reports/pages/customers/generic/eos/GenericEOS";
import MeritmedicaltijuanaEOS from "reports/pages/customers/meritmedical-tijuana/eos/MeritmedicaltijuanaEOS";
import EccomfglangleyEOS from "reports/pages/customers/eccomfg-langley/eos/EccomfglangleyEOS";
import EccomfgcalgaryEOS from "reports/pages/customers/eccomfg-calgary/eos/EccomfgcalgaryEOS";
import PciEos from "reports/pages/customers/pci/eos/PciEos";
import { ReportsListingRouter } from "./ReportsListingRouter";
import { ReportParams } from "reports/types";
import { TraceProvider } from "providers/TraceProvider";
import { usePopulateCustomerJsonsInAtom } from "components/Dashboard/Tiles/CustomerTiles/CustomerTilesInternalTools/CustomerInfoIndicator";
import styled from "styled-components/macro";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";

export const ReportsRouter = () => {
  // Populate customer json for this tenant
  const { ckey } = useParams<ReportParams>();
  usePopulateCustomerJsonsInAtom(
    ckey ? [{ abbrevName: ckey, customerName: "" }] : []
  );
  const [
    {
      general: { customCss },
      reportConfig,
    },
  ] = useFlightConfig();
  const reportsCustomCss = reportConfig?.customCss ?? "";
  return (
    <Styles $customCss={customCss} $reportsCustomCss={reportsCustomCss}>
      <TraceProvider>
        <Routes>
          <Route path="/" element={<ReportsListingRouter />} />

          {/* End of Shift */}
          <Route
            path="eos/:processGroupName?"
            element={<EOSComponentForTenant />}
          />

          {/* Week to Date  */}
          <Route
            path="week-to-date/:year?/:week?"
            element={<WeekToDateReportRoute />}
          />

          {/* PCI E5, H5, H8, E2, H1 */}
          <Route
            path="focusroom/:machineName"
            element={<PciMachinePipeline />}
          />

          {/* SKU and Metrics Report */}
          <Route
            path="sku-metrics/:processGroupName?"
            element={<SkumetricsComponentForTenant />}
          />

          {/* Nobel Weekly Report for Operator TPM Tag */}
          <Route path="OperatorTPM/:shift" element={<NbOperatorTPM />} />

          {/* Qiagen Quarterly Report */}
          <Route path="Quarterly" element={<Quarterly />} />

          {/* Steris Weekly */}
          <Route path="weekly" element={<SterisWeekly />} />

          {/* Last N Months Data */}
          <Route
            path="performance/monthly/:processGroupName"
            element={<MonthlyPerformanceReport />}
          />

          {/* Tagging Report */}
          <Route path="tagging" element={<TaggingComponentForTenant />} />
        </Routes>
      </TraceProvider>
    </Styles>
  );
};
const Styles = styled.div<{ $customCss: string; $reportsCustomCss: string }>`
  /* User selection enabled only for reports */
  * {
    user-select: text;
  }
  ${({ $customCss }) => $customCss}
  ${({ $reportsCustomCss }) => $reportsCustomCss}
`;
/** Maps tenant name to EOS Component */
const CustomEOSTenantToComponentMapping = {
  "eccomfg-langley": <EccomfglangleyEOS />,
  "eccomfg-calgary": <EccomfgcalgaryEOS />,
  kruger: <KrugerEOS />,
  "meritmedical-tijuana": <MeritmedicaltijuanaEOS />,
  naturesway: <NWEos />,
  pci: <PciEos />,
  pciuk: <PciukEos />,
  sbdwarrington: <SbdwarringtonEOS />,
};

function EOSComponentForTenant() {
  const { ckey: tenant } = useParams<ReportParams>();

  return tenant && CustomEOSTenantToComponentMapping[tenant] ? (
    CustomEOSTenantToComponentMapping[tenant]
  ) : (
    <GenericEOS />
  );
}

function TaggingComponentForTenant() {
  const { ckey: tenant } = useParams<ReportParams>();
  return tenant === "kruger" ? (
    <KrugerTagging />
  ) : (
    <Navigate replace={true} to="/" />
  );
}

function SkumetricsComponentForTenant() {
  const { ckey: tenant } = useParams<ReportParams>();
  return tenant === "pciuk" ? <PciukWeeklySku /> : <SkuAndMetricReport />;
}
